import { LocalStorageOptions } from "@pathwright/utils/src/storage/useLocalStorage"
import { FormikValues, useFormikContext } from "formik"
import defaultsDeep from "lodash/defaultsDeep"
import { useEffect, useMemo, useRef } from "react"
import useDiscardFormState, {
  DiscardFormStateReturn,
  SetFormState
} from "./useDiscardFormState"
import { FormStateKey, FormStateStorage } from "./useFormStateStorage"

export type OnChangeDiscardFormState = (
  discardFormState: DiscardFormStateReturn
) => void

export type FormikDiscardFormStateOptions = {
  // Optionally limit the keys that are stored. Only those specified keys will be stored
  // and compared for determining if form state has changed.
  keys?: (string | number)[]
  formStateKey: FormStateKey
  formStateMigrationKey?: string
  localStorageOptions?: LocalStorageOptions
  onChangeDiscardFormState?: OnChangeDiscardFormState
}

// Reduce the values per the provided `keys`.
// If no `keys`, then retain all values.
export function pickFormValues(
  values: any,
  keys?: FormikDiscardFormStateOptions["keys"]
) {
  if (keys) {
    return keys.reduce((acc, key) => {
      acc[key] = values?.[key]
      return acc
    }, {} as FormikValues)
  }
  return values
}

// Handles setting initial formik state based on stored form state and
// synchronizing formik state with stored form state.
const useFormikDiscardFormState = ({
  keys,
  formStateKey,
  formStateMigrationKey,
  localStorageOptions,
  onChangeDiscardFormState
}: FormikDiscardFormStateOptions) => {
  const formikContext = useFormikContext()
  const needsDraftValidation = useRef(false)

  // Wait for restored values to commit. A timer can run before React commits
  // setValues, leaving the blank mount-time validation as the final result.
  // This effect precedes useDiscardFormState's restoration effect so it only
  // consumes the flag on the subsequent render, using the latest values.
  useEffect(() => {
    if (needsDraftValidation.current) {
      needsDraftValidation.current = false
      void formikContext.validateForm(formikContext.values)
    }
  }, [formikContext.values])

  // formState naturally maps to Formik's context.
  const formState: FormStateStorage = useMemo(
    () => ({
      values: pickFormValues(formikContext.values, keys),
      initialValues: pickFormValues(formikContext.initialValues, keys)
    }),
    [formikContext.values, formikContext.initialValues]
  )

  const setFormState: SetFormState = (
    // TODO: get type of `nextState` from Formik.
    nextState
  ) => {
    // Use resetForm or setValues depending on if Formik should
    //   1) reset to initial state or reset to a new state
    //   2) update to a modified state
    // Only necessary since resetForm does not support handling #2.
    if (
      nextState.values === formikContext.values ||
      nextState.values === nextState.initialValues
    ) {
      formikContext.resetForm(defaultsDeep(nextState, formikContext))
      formikContext.validateForm(nextState.values)
    } else {
      const nextValues = defaultsDeep(nextState.values, formikContext.values)
      needsDraftValidation.current = true
      void formikContext.setValues(nextValues, false /* shouldValidate */)
    }
  }

  const discardFormState = useDiscardFormState({
    key: formStateKey,
    formStateMigrationKey,
    formState,
    setFormState,
    localStorageOptions
  })

  // Send change upstream, only when discardCount changes.
  useEffect(() => {
    onChangeDiscardFormState?.(discardFormState)
  }, [discardFormState.discardCount])

  return discardFormState
}

export default useFormikDiscardFormState
